"use client";

import { Check } from "lucide-react";

import { ISO_4217_CURRENCIES } from "@/features/currency/_logic/iso-4217";

import { useEstimator } from "../../_context/EstimatorContext";
import { CABIN_OPTIONS } from "../../_logic/constants";
import { PopoverSelect } from "../ui/PopoverSelect";
import { AirlinesEditor } from "./AirlinesEditor";

/**
 * The trailing "Economy · USD" popover: the default cabin for everyone, the display currency,
 * and which airlines fares may use.
 * @param props.onPicked - Called after a cabin is picked, to close the popover.
 * @returns The editor.
 * @throws Never.
 */
export const TripOptionsEditor = ({ onPicked }: { onPicked: () => void }) => {
  // Hooks
  const { workspace, rates, edit } = useEstimator();

  // Derived
  const currencyOptions = ISO_4217_CURRENCIES.filter((currency) => currency.code === "USD" || rates[currency.code]).map(
    (currency) => ({ value: currency.code, label: currency.code, sublabel: currency.name, keywords: currency.name })
  );

  // Render
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold text-gray-900">Default cabin</h3>
          <p className="text-xs font-medium text-gray-600">Change it per group under Who.</p>
        </div>
        <ul className="flex list-none flex-col">
          {CABIN_OPTIONS.map((option) => (
            <li key={option.value}>
              {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
              <button
                type="button"
                aria-pressed={workspace.cabin === option.value}
                onClick={() => {
                  edit({ type: "setCabin", cabin: option.value }, "user");
                  onPicked();
                }}
                className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 aria-pressed:text-gray-900"
              >
                {option.label}
                {workspace.cabin === option.value && <Check className="size-4 text-indigo-600" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
        <h3 className="text-sm font-semibold text-gray-900">Currency</h3>
        <PopoverSelect
          ariaLabel="Currency"
          options={currencyOptions}
          value={workspace.currency}
          searchPlaceholder="Find currency..."
          onChange={(currency) => edit({ type: "setCurrency", currency }, "user")}
        />
      </div>
      <AirlinesEditor />
    </>
  );
};
